import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/free-solid-svg-icons';
import ParticleNetwork from '../components/ParticleNetwork';
import WorldMap from '../components/WorldMap';
import '../styles/ExportsPage.css';

const ExportsPage = () => {
    const { t } = useTranslation();
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [hoveredCountry, setHoveredCountry] = useState(null);
    const activeCountry = hoveredCountry || selectedCountry;

    const statistics = [
        { id: 1, value: '30+', label: t('exports.stats.countries') },
        { id: 2, value: '1000+', label: t('exports.stats.tons') },
        { id: 3, value: '%100', label: t('exports.stats.satisfaction') },
    ];

    const exportCountries = [
        'germany',
        'france',
        'italy',
        'spain',
        'netherlands',
        'belgium',
        'united_kingdom',
        'united_states',
        'russia',
        'azerbaijan',
        'georgia',
        'iraq',
        'qatar',
        'saudi_arabia',
        'united_arab_emirates',
        'romania',
        'bulgaria'
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { type: "spring", stiffness: 100, damping: 15 }
        }
    };

    return (
        <div className="exports-page">
            {/* Simple Hero Section */}
            <section className="exports-hero-simple">
                <ParticleNetwork />
                <div className="container">
                    <motion.div
                        className="hero-centered-content"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        <div className="breadcrumbs">
                            <Link to="/">{t('nav.home')}</Link>
                            <span className="separator">/</span>
                            <span className="current">{t('nav.exports')}</span>
                        </div>

                        <motion.div className="hero-main" variants={itemVariants}>
                            <h1 className="hero-title">{t('exports.hero_title')}</h1>
                            <div className="hero-accent-line"></div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            <section className="exports-stats">
                <div className="container">
                    <div className="stats-grid">
                        {statistics.map((stat, index) => (
                            <motion.div
                                key={stat.id}
                                className="stat-card"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.2 }}
                                whileHover={{ y: -10 }}
                            >
                                <div className="stat-value">{stat.value}</div>
                                <div className="stat-label">{stat.label}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="exports-map-section">
                <div className="container">
                    <div className="section-header text-center">
                        <h2>{t('exports.map_title')}</h2>
                        <p>{t('exports.map_desc')}</p>
                    </div>

                    <div className="exports-map-layout">
                        <motion.div
                            className="map-panel"
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.7 }}
                        >
                            <WorldMap activeCountry={activeCountry} />
                        </motion.div>

                        <motion.aside
                            className="countries-panel"
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                        >
                            <div className="countries-panel-header">
                                <span className="countries-icon">
                                    <FontAwesomeIcon icon={faLocationDot} />
                                </span>
                                <div>
                                    <h3>{t('exports.country_list_title')}</h3>
                                    <p>{t('exports.country_list_desc')}</p>
                                </div>
                            </div>

                            <ul className="countries-list">
                                {exportCountries.map((countryKey, index) => (
                                    <motion.li
                                        key={countryKey}
                                        className={activeCountry === countryKey ? 'active' : ''}
                                        onMouseEnter={() => setHoveredCountry(countryKey)}
                                        onFocus={() => setHoveredCountry(countryKey)}
                                        onMouseLeave={() => setHoveredCountry(null)}
                                        onBlur={() => setHoveredCountry(null)}
                                        onClick={() => setSelectedCountry(countryKey)}
                                        tabIndex={0}
                                        initial={{ opacity: 0, y: 12 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: 0.04 * index }}
                                    >
                                        <span>{index + 1}</span>
                                        {t(`exports.countries.${countryKey}`)}
                                    </motion.li>
                                ))}
                            </ul>
                        </motion.aside>
                    </div>
                </div>
            </section>

        </div>
    );
};

export default ExportsPage;
